import React from 'react';
import { Select, SelectItem, Spinner } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import { Department } from '@/services/apis/departments';

interface DepartmentSelectorProps {
  departments: Department[];
  isLoading?: boolean;
  error?: Error | null;
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  isInvalid?: boolean;
  errorMessage?: string;
  isRequired?: boolean;
  label?: string;
  variant?: 'flat' | 'bordered' | 'faded' | 'underlined';
  className?: string;
  'aria-label'?: string;
  showEmptyOption?: boolean;
  isDisabled?: boolean;
}

const DepartmentSelector: React.FC<DepartmentSelectorProps> = ({
  departments,
  isLoading = false,
  error,
  value,
  onChange,
  placeholder,
  isInvalid = false,
  errorMessage,
  isRequired = false,
  label,
  variant,
  className,
  'aria-label': ariaLabel,
  showEmptyOption = true,
  isDisabled = false,
}) => {
  const { t } = useTranslation('admin');

  const renderSelectItems = () => {
    if (isLoading) {
      return <SelectItem key="loading">{t('common.loading')}</SelectItem>;
    }

    if (error) {
      return <SelectItem key="error">{error?.message || 'Failed to load departments'}</SelectItem>;
    }

    return (
      <>
        {departments.map((department) => (
          <SelectItem key={department.id}>{department.name}</SelectItem>
        ))}
      </>
    );
  };

  const selectLabel = label;
  const selectPlaceholder =
    placeholder || t('users.form.department.placeholder', { defaultValue: 'Select a department' });
  const selectAriaLabel = ariaLabel || (label === '' ? selectPlaceholder : undefined);

  return (
    <Select
      label={selectLabel}
      placeholder={selectPlaceholder}
      selectedKeys={value !== undefined && value !== null ? [value] : []}
      onSelectionChange={(keys) => {
        const selectedValue = Array.from(keys)[0] as string;
        onChange(selectedValue ?? '');
      }}
      isRequired={isRequired}
      variant={variant}
      isInvalid={isInvalid}
      errorMessage={errorMessage}
      isLoading={isLoading}
      startContent={isLoading ? <Spinner size="sm" /> : undefined}
      className={className}
      selectionMode="single"
      isDisabled={isDisabled}
      aria-label={selectAriaLabel || selectLabel || t('users.form.department.label', { defaultValue: 'Department' })}
    >
      {showEmptyOption ? <SelectItem key="">{t('common.none') || 'None'}</SelectItem> : null}
      {renderSelectItems()}
    </Select>
  );
};

export default DepartmentSelector;
